import * as React from 'react';
import { useState, useEffect } from 'react';
import { Formik, Form, Field, useField } from 'formik';
import styled, { css } from 'styled-components';
import URI from 'urijs';

import { SAMLConfig } from '@graylog/enterprise-api';

import { validateField } from 'util/FormsUtils';
import { Wizard, FormikFormGroup, InputOptionalInfo as Opt, Spinner, Select, MultiSelect } from 'components/common';
import { Row, Col, ButtonToolbar, Button, Input, Alert } from 'components/bootstrap';
import {
  AUTH_SERVICE_TYPE,
  SSO_SAML_PATH,
  SSO_SAML_ACS_PATH,
  SSO_SAML_LOGOUT_PATH,
} from 'authentication/components/saml/constants';
import formatAuthBackendValidationErrors from 'authentication/helpers/formatAuthBackendValidationErrors';
import samlAuthBackendSubmit from 'authentication/components/saml/config/helpers/samlAuthBackendSubmit';
import AuthBackendSubmitError from 'authentication/components/AuthBackendSubmitError';
import Routes from 'routing/Routes';
import useHistory from 'routing/useHistory';
import useRoles from 'common/hooks/useRoles';
import AppConfig from 'util/AppConfig';
import type FetchError from 'logic/errors/FetchError';
import SAMLPEMField from 'authentication/components/saml/config/SAMLPEMField';
import useSAMLDefaultAttributeMapping from 'authentication/components/saml/hooks/useSAMLDefaultAttributeMapping';
import SAMLCertInfoTable from 'authentication/components/saml/config/SAMLCertInfoTable';
import { getPathnameWithoutId } from 'util/URLUtils';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import useLocation from 'routing/useLocation';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';

import type { SAMLBackendFormValues, SAMLCertInfo } from './types';

type StepKey = 'idp_config' | 'team_sync';

// The SP base URL will almost always be the URL of the server.
const DEFAULT_SP_BASE_URL = `${window.location.origin}${AppConfig.gl2AppPathPrefix()}`;

const normalizeURI = (url: string) => new URI(url).normalizePath().toString();

const StyledDefList = styled.dl(
  ({ theme }) => css`
    dd {
      margin-bottom: ${theme.spacings.sm};
    }
  `,
);

const StyledSectionWrapper = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.sm};

    p {
      margin-top: ${theme.spacings.sm};
    }
  `,
);

const AttributeMapping = ({ title, mappings }: { title: string; mappings: string[] }) => (
  <>
    <strong>{title}</strong>
    <ul>
      {(mappings ?? []).sort().map((attributeName) => (
        <li key={attributeName}>
          <code>{attributeName}</code>
        </li>
      ))}
    </ul>
  </>
);

const FormSection = ({ title, children = undefined }: { title: string; children?: any }) => (
  <Row>
    <Col md={9} mdOffset={3}>
      <StyledSectionWrapper>
        <hr />
        <h3>{title}</h3>
        {children}
      </StyledSectionWrapper>
    </Col>
  </Row>
);

const BackendFetchError = ({ message, title }) => {
  if (!message) {
    return null;
  }

  return (
    <Row>
      <Col md={9} mdOffset={3}>
        <Alert bsStyle="danger" style={{ wordBreak: 'break-word' }} title={title}>
          {message}
        </Alert>
      </Col>
    </Row>
  );
};

const CertificateInfo = ({ certInfo, error = undefined }: { certInfo: SAMLCertInfo; error?: string }) => {
  if (error) {
    return <Alert bsStyle="danger">{error}</Alert>;
  }

  if (!certInfo) {
    return null;
  }

  return (
    <SAMLCertInfoTable
      certificate={certInfo.certificate}
      subject={certInfo.subject}
      issuer={certInfo.issuer}
      sha256Fingerprint={certInfo.sha256_fingerprint}
      sha1Fingerprint={certInfo.sha1_fingerprint}
      validFrom={certInfo.valid_from}
      validTo={certInfo.valid_to}
    />
  );
};

const CertificateInfoRow = ({ certInfo, error = undefined }: { certInfo: SAMLCertInfo; error?: string }) => (
  <Row>
    <Col md={9} mdOffset={3}>
      <CertificateInfo certInfo={certInfo} error={error} />
    </Col>
  </Row>
);

const GenerateKeyPairButton = ({ values, onClick }: { values: SAMLBackendFormValues; onClick: () => void }) => {
  const { spCredentialCertificate, spCredentialPrivateKey } = values;

  const isDestructive = spCredentialCertificate || spCredentialPrivateKey?.is_set || spCredentialPrivateKey?.set_value;

  return (
    <Button type="button" bsStyle={isDestructive ? 'danger' : 'info'} onClick={onClick}>
      {isDestructive ? 'Regenerate' : 'Generate'} Certificate and Key
    </Button>
  );
};

const ValidationErrorSummary = ({ errors }) => {
  if (!errors || Object.keys(errors).length === 0) {
    return null;
  }

  return (
    <Row>
      <Col md={9} mdOffset={3}>
        <Alert title="Validation Errors" bsStyle="danger">
          The form has validation errors. Please check all form fields above.
        </Alert>
      </Col>
    </Row>
  );
};

const MetadataRefreshInfo = ({ show }: { show: boolean }) => {
  if (!show) {
    return null;
  }

  return (
    <Alert bsStyle="info" style={{ margin: 0 }}>
      The IdP metadata will be refreshed on every login, so the following fields can&apos;t be changed.
    </Alert>
  );
};

const fetchCertInfo = (
  cert: string,
  setValue: (info: SAMLCertInfo | undefined) => void,
  setError: (error: string) => void,
) => {
  if (cert) {
    SAMLConfig.getCertificateInfo({ certificate: cert })
      .then((info) => {
        setValue(info);
        setError(undefined);
      })
      .catch((error) => {
        setValue(undefined);
        setError(
          error?.additional?.body?.message ||
            'Error fetching certificate info. Please check that the file is a valid PEM file.',
        );
      });
  } else {
    setValue(undefined);
    setError(undefined);
  }
};

type Step = 'idp_config' | 'team_sync';

const IdPConfigForm = ({
  values,
  roles,
  isSubmitting,
  isFetchingMetadata,
  validationErrors,
  submitErrors,
  metadataFetchErrors,
  spCredentialsGenerateErrors,
  setActiveStep,
  setFieldValue,
  handleMetadataFetch,
  handleSpCredentialFetch,
  defaultAttributeMapping,
}) => {
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  useEffect(() => {
    fetchCertInfo(
      values.idpCertificate,
      (info) => setFieldValue('idpCertificateInfo', info),
      (error) => setFieldValue('idpCertificateInfoError', error),
    );
  }, [values.idpCertificate, setFieldValue]);
  useEffect(() => {
    fetchCertInfo(
      values.spCredentialCertificate,
      (info) => setFieldValue('spCredentialCertificateInfo', info),
      (error) => setFieldValue('spCredentialCertificateInfoError', error),
    );
  }, [values.spCredentialCertificate, setFieldValue]);

  const rolesOptions = roles.map((role) => ({ label: role.name, value: role.id }));
  const { idpMetadataRefreshAtLogin: isRefreshAtLogin } = values;

  return (
    <>
      <Col md={7}>
        <Form className="form form-horizontal">
          <FormikFormGroup
            name="title"
            label="Title"
            placeholder="Title"
            disabled={isSubmitting}
            validate={validateField({ required: true })}
          />
          <FormikFormGroup
            type="textarea"
            name="description"
            label={
              <>
                Description <Opt />
              </>
            }
            placeholder="Description"
            disabled={isSubmitting}
          />

          <FormSection title="Identity Provider Config" />

          <FormikFormGroup
            name="idpMetadataUrl"
            label="Metadata URL"
            placeholder="Metadata URL"
            help="The SAML metadata URL of your IdP."
            disabled={isSubmitting || isFetchingMetadata}
            validate={validateField({ url: true })}
            buttonAfter={
              <Button
                type="button"
                bsStyle={values.idpMetadataUrl ? 'info' : 'default'}
                disabled={!values.idpMetadataUrl}
                onClick={handleMetadataFetch(values, setFieldValue)}>
                Fetch
              </Button>
            }
          />

          <BackendFetchError title="Failed to fetch IdP metadata:" message={metadataFetchErrors} />

          <Input
            label="Metadata Refresh At Login"
            id="idp-metadata-refresh-at-login-checkbox"
            labelClassName="col-sm-3"
            wrapperClassName="col-sm-9">
            <FormikFormGroup
              type="checkbox"
              name="idpMetadataRefreshAtLogin"
              label=""
              help="Select this option if you want to refresh the IdP metadata on every login."
              wrapperClassName="col-sm-12"
            />
            <MetadataRefreshInfo show={isRefreshAtLogin} />
          </Input>

          <FormikFormGroup
            name="idpSsoServiceRedirectBindingUrl"
            label="SSO Service Redirect URL"
            placeholder="Single Sign-On Service Redirect Binding URL"
            help="The Single Sign-On Service Redirect Binding URL of your IdP."
            disabled={isSubmitting || isFetchingMetadata || isRefreshAtLogin}
            validate={validateField({ url: true })}
          />

          <FormikFormGroup
            name="idpSsoServicePostBindingUrl"
            label="SSO Service POST URL"
            placeholder="Single Sign-On Service POST Binding URL"
            help="The Single Sign-On Service POST Binding URL of your IdP."
            disabled={isSubmitting || isFetchingMetadata || isRefreshAtLogin}
            validate={validateField({ url: true })}
          />

          <FormikFormGroup
            name="idpLogoutServiceRedirectBindingUrl"
            label="Logout Service Redirect URL"
            placeholder="Single Logout Service Redirect Binding URL"
            help="The Single Logout Service Redirect Binding URL of your IdP."
            disabled={isSubmitting || isFetchingMetadata || isRefreshAtLogin}
            validate={validateField({ url: true })}
          />

          <FormikFormGroup
            name="idpLogoutServicePostBindingUrl"
            label="Logout Service POST URL"
            placeholder="Single Logout Service POST Binding URL"
            help="The Single Logout Service POST Binding URL of your IdP."
            disabled={isSubmitting || isFetchingMetadata || isRefreshAtLogin}
            validate={validateField({ url: true })}
          />

          <FormikFormGroup
            name="idpEntityId"
            label="Entity ID"
            placeholder="Entity ID"
            help="The entity ID of your IdP."
            disabled={isSubmitting || isFetchingMetadata || isRefreshAtLogin}
            validate={validateField({ required: true })}
          />

          <SAMLPEMField
            name="idpCertificate"
            filename="idpCertificateFilename"
            label="Certificate"
            help="The signing certificate of your IdP."
            isValueSet={!!values.idpCertificate}
            disabled={isRefreshAtLogin}
          />

          <CertificateInfoRow certInfo={values.idpCertificateInfo} error={values.idpCertificateInfoError} />

          <FormSection title="Service Provider Config" />

          <FormikFormGroup
            name="spBaseUrl"
            label="Base URL"
            placeholder="Base URL"
            help={
              <span>
                The base URL of your setup for this configuration. The URL is used for SAML endpoints.{' '}
                <strong>You usually don&apos;t need to change this value.</strong>
              </span>
            }
            disabled={isSubmitting}
            validate={validateField({ required: true, url: true })}
          />

          <FormikFormGroup
            name="spEntityId"
            label="Entity ID"
            placeholder="Entity ID"
            help="The entity ID for this SP configuration."
            disabled={isSubmitting}
            validate={validateField({ required: true })}
          />

          <SAMLPEMField
            name="spCredentialCertificate"
            filename="spCredentialCertificateFilename"
            label="Certificate"
            help="The signing certificate for this SP configuration."
            isValueSet={!!values.spCredentialCertificate}>
            Use the button below to generate a new key pair, drag a PEM file here or click to select a PEM file
          </SAMLPEMField>

          <CertificateInfoRow
            certInfo={values.spCredentialCertificateInfo}
            error={values.spCredentialCertificateInfoError}
          />

          <SAMLPEMField
            name="spCredentialPrivateKey"
            filename="spCredentialPrivateKeyFilename"
            label="Private Key"
            help="The signing key for this SP configuration."
            isValueSet={values.spCredentialPrivateKey?.is_set || values.spCredentialPrivateKey?.set_value}
            encrypted>
            Use the button below to generate a new key pair, drag a PEM file here or click to select a PEM file
          </SAMLPEMField>

          <Input id="sp-generate-credential" label={' '} labelClassName="col-sm-3" wrapperClassName="col-sm-9">
            <GenerateKeyPairButton values={values} onClick={handleSpCredentialFetch(setFieldValue)} />
          </Input>

          <BackendFetchError title="Failed to generate SP credentials:" message={spCredentialsGenerateErrors} />

          <FormSection title="User Attribute Mapping">
            <p>
              By default, the authentication service tries to extract commonly used user attribute names from the SAML
              authentication response. You can define custom attribute names if the defaults don&apos;t work.
            </p>
          </FormSection>

          <FormikFormGroup
            name="idpUserNameAttribute"
            label={
              <>
                User Name <Opt />
              </>
            }
            placeholder="User Name attribute name"
            help="The User Name attribute name."
            disabled={isSubmitting}
          />

          <FormikFormGroup
            name="idpEmailAttribute"
            label={
              <>
                Email <Opt />
              </>
            }
            placeholder="Email attribute name"
            help="The Email attribute name."
            disabled={isSubmitting}
          />

          <FormikFormGroup
            name="idpFirstNameAttribute"
            label={
              <>
                First Name <Opt />
              </>
            }
            placeholder="First Name attribute name"
            help="The First Name attribute name."
            disabled={isSubmitting}
          />

          <FormikFormGroup
            name="idpLastNameAttribute"
            label={
              <>
                Last Name <Opt />
              </>
            }
            placeholder="Last Name attribute name"
            help="The Last Name attribute name."
            disabled={isSubmitting}
          />

          <FormikFormGroup
            name="idpFullNameAttribute"
            label={
              <>
                Full Name <Opt />
              </>
            }
            placeholder="Full Name attribute name"
            help="The Full Name attribute name."
            disabled={isSubmitting}
          />

          <FormSection title="Roles Configuration" />

          <Field name="defaultRoles" validate={validateField({ required: true })}>
            {({ field: { name, value, onChange, onBlur }, meta: { error } }) => (
              <Input
                bsStyle={error ? 'error' : undefined}
                help={
                  <span>
                    The default roles synchronized user will obtain. All users need the <code>Reader</code> role, to use
                    the web interface
                  </span>
                }
                error={error}
                id="default-roles-select"
                label="Default Roles"
                labelClassName="col-sm-3"
                wrapperClassName="col-sm-9">
                <Select
                  multi
                  onBlur={onBlur}
                  onChange={(selectedRoles) => onChange({ target: { value: selectedRoles, name } })}
                  options={rolesOptions}
                  placeholder="Search for roles"
                  value={value}
                />
              </Input>
            )}
          </Field>

          {validationErrors}
          {submitErrors}

          <ButtonToolbar className="pull-right">
            <Button
              disabled={isSubmitting}
              onClick={() => {
                sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_IDP_CONFIG_SAVE_CLICKED, {
                  app_pathname: getPathnameWithoutId(pathname),
                  app_section: 'saml-idp-configuration-step',
                  app_action_value: 'save',
                });
              }}
              type="submit">
              Finish & Save Service
            </Button>
            <Button
              bsStyle="primary"
              disabled={isSubmitting}
              onClick={() => {
                sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_NEXT_TEAM_SYNC_CLICKED, {
                  app_pathname: getPathnameWithoutId(pathname),
                  app_section: 'saml-idp-configuration-step',
                  app_action_value: 'next-step',
                });

                setActiveStep('team_sync');
              }}
              type="button">
              Next: Team Sync
            </Button>
          </ButtonToolbar>
        </Form>
      </Col>
      <Col md={5}>
        <Alert bsStyle="info">
          The following Service Provider information is usually required to configure your Identity Provider.
        </Alert>
        <StyledDefList>
          <dt>Service Provider Entity ID:</dt>
          <dd>{values.spEntityId ? <code>{values.spEntityId}</code> : <em>Please add a SP Entity ID value</em>}</dd>
          <dt>Service Provider Assertion Consumer Service (ACS) URL:</dt>
          <dd>
            {values.spBaseUrl ? (
              <code>{normalizeURI(`${values.spBaseUrl}/${SSO_SAML_ACS_PATH}`)}</code>
            ) : (
              <em>Please add a SP Base URL value</em>
            )}
          </dd>
          <dt>Service Provider Single Logout Service URL:</dt>
          <dd>
            {values.spBaseUrl ? (
              <code>{normalizeURI(`${values.spBaseUrl}/${SSO_SAML_LOGOUT_PATH}`)}</code>
            ) : (
              <em>Please add a SP Base URL value</em>
            )}
          </dd>
          <dt>Service Provider Certificate:</dt>
          <dd>
            {(values.spCredentialCertificateInfo && (
              <CertificateInfo certInfo={values.spCredentialCertificateInfo} />
            )) || <em>Please generate or upload Service Provider credentials!</em>}
          </dd>
        </StyledDefList>

        <StyledSectionWrapper>
          <hr />
          <h3>Default Attribute Mappings</h3>
          {Object.keys(defaultAttributeMapping).length === 0 ? (
            <Alert bsStyle="danger">Could&apos;t load default attribute mappings from the API.</Alert>
          ) : (
            <>
              <p>
                The authentication service tries to extract the following user attributes from the SAML authentication
                response by default.
              </p>
              <AttributeMapping title="User Name" mappings={defaultAttributeMapping.user_name} />
              <AttributeMapping title="Email" mappings={defaultAttributeMapping.email} />
              <AttributeMapping title="First Name" mappings={defaultAttributeMapping.first_name} />
              <AttributeMapping title="Last Name" mappings={defaultAttributeMapping.last_name} />
              <AttributeMapping title="Full Name" mappings={defaultAttributeMapping.full_name} />
            </>
          )}
        </StyledSectionWrapper>
      </Col>
    </>
  );
};

const TeamSyncForm = ({ isSubmitting, defaultAttributeMapping, setActiveStep, licenseIsValid }) => {
  const { pathname } = useLocation();
  const [{ value: isTeamSyncEnabled }] = useField('teamSyncEnabled');
  const sendTelemetry = useSendTelemetry();

  return (
    <>
      <Col md={7}>
        {!licenseIsValid && <Alert bsStyle="warning">Team sync is only configurable with a valid license!</Alert>}
        <Form className="form form-horizontal">
          <Input label="Enable Team Sync" id="team-sync-checkbox" labelClassName="col-sm-3" wrapperClassName="col-sm-9">
            <FormikFormGroup
              label=""
              type="checkbox"
              wrapperClassName="col-sm-12"
              name="teamSyncEnabled"
              help="Select this option if you want to import groups from the SAML response as teams."
              disabled={isSubmitting || !licenseIsValid}
            />
          </Input>

          <Row>
            <Col md={9} mdOffset={3}>
              <StyledSectionWrapper>
                <p>
                  By default, the authentication service tries to extract the user groups using commonly used group
                  attribute names from the SAML authentication response. You can define a custom group attribute name if
                  the defaults don&apos;t work.
                </p>
              </StyledSectionWrapper>
            </Col>
          </Row>

          <FormikFormGroup
            name="teamSyncGroupAttributeName"
            label="Group Attribute Name"
            placeholder="Group Attribute Name"
            help="Add a SAML assertion attribute name that contains a list of group names."
            disabled={isSubmitting || !licenseIsValid || !isTeamSyncEnabled}
          />

          <Field name="teamSyncAllowedGroups">
            {({ field: { name, value, onChange, onBlur }, meta: { error } }) => (
              <Input
                bsStyle={error ? 'error' : undefined}
                help={
                  <span>
                    You can restrict the groups that are allowed to log in by adding them here. If no group is added,
                    all groups are allowed.
                  </span>
                }
                error={error}
                id="allowed-groups"
                label="Allowed Groups"
                labelClassName="col-sm-3"
                wrapperClassName="col-sm-9">
                <MultiSelect
                  id="allowed-groups-select"
                  value={value}
                  // @ts-ignore
                  formatCreateLabel={(label: string) => `Add group "${label}"?`}
                  onBlur={onBlur}
                  placeholder="Type group name"
                  options={[]}
                  onChange={(selectedGroups) => onChange({ target: { value: selectedGroups, name } })}
                  disabled={isSubmitting || !licenseIsValid || !isTeamSyncEnabled}
                  allowCreate
                />
              </Input>
            )}
          </Field>

          <ButtonToolbar className="pull-right">
            <Button
              disabled={isSubmitting}
              onClick={() => {
                sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_PREVIOUS_IDP_CONFIG_CLICKED, {
                  app_pathname: getPathnameWithoutId(pathname),
                  app_section: 'saml-team-sync-step',
                  app_action_value: 'previous-step',
                });

                setActiveStep('idp_config');
              }}
              type="button">
              Previous: IdP Configuration
            </Button>
            <Button
              bsStyle="primary"
              disabled={isSubmitting}
              onClick={() => {
                sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_TEAM_SYNC_SAVE_CLICKED, {
                  app_pathname: getPathnameWithoutId(pathname),
                  app_section: 'saml-team-sync-step',
                  app_action_value: 'save',
                });
              }}
              type="submit">
              Finish & Save Service
            </Button>
          </ButtonToolbar>
        </Form>
      </Col>
      <Col md={5}>
        <StyledSectionWrapper>
          <h3>Default Group Attribute Mappings</h3>
          {Object.keys(defaultAttributeMapping).length === 0 ? (
            <Alert bsStyle="danger">Could&apos;t load default group attribute mappings from the API.</Alert>
          ) : (
            <>
              <p>
                The authentication service tries to extract the user groups from the following attributes from the SAML
                authentication response by default.
              </p>
              <ul>
                {(defaultAttributeMapping.groups ?? []).sort().map((attributeName) => (
                  <li key={attributeName}>
                    <code>{attributeName}</code>
                  </li>
                ))}
              </ul>
            </>
          )}
        </StyledSectionWrapper>
      </Col>
    </>
  );
};

interface WizardProps {
  authBackend?: SAMLBackendFormValues;

  initialStep?: string;

  licenseIsValid: boolean;
}

const defaultValues: SAMLBackendFormValues = {
  type: AUTH_SERVICE_TYPE,
  title: '',
  description: '',
  defaultRoles: '',
  idpMetadataUrl: '',
  idpMetadataRefreshAtLogin: false,
  idpSsoServiceRedirectBindingUrl: '',
  idpSsoServicePostBindingUrl: '',
  idpLogoutServiceRedirectBindingUrl: undefined,
  idpLogoutServicePostBindingUrl: undefined,
  idpEntityId: '',
  idpCertificate: '',
  idpCertificateFilename: '',
  idpCertificateInfo: undefined,
  idpCertificateInfoError: undefined,
  idpUserNameAttribute: undefined,
  idpEmailAttribute: undefined,
  idpFirstNameAttribute: undefined,
  idpLastNameAttribute: undefined,
  idpFullNameAttribute: undefined,
  spBaseUrl: DEFAULT_SP_BASE_URL,
  spEntityId: normalizeURI(`${DEFAULT_SP_BASE_URL}${SSO_SAML_PATH}`),
  spCredentialCertificate: '',
  spCredentialCertificateFilename: '',
  spCredentialCertificateInfo: undefined,
  spCredentialCertificateInfoError: undefined,
  spCredentialPrivateKey: { is_set: false },
  spCredentialPrivateKeyFilename: '',
  teamSyncIsCurrentlyEnabled: false,
  teamSyncEnabled: false,
  teamSyncGroupAttributeName: undefined,
  teamSyncAllowedGroups: undefined,
};

const SAMLBackendConfigWizard = ({
  authBackend = defaultValues,
  initialStep = 'idp_config',
  licenseIsValid,
}: WizardProps) => {
  const [activeStep, setActiveStep] = useState<Step>(initialStep as Step);
  const [submitErrors, setSubmitErrors] = useState(undefined);
  const [metadataFetchErrors, setMetadataFetchErrors] = useState<string>(undefined);
  const [spCredentialsGenerateErrors, setSpCredentialsGenerateErrors] = useState<string>(undefined);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState<boolean>(false);
  const { isLoading, roles } = useRoles();
  const history = useHistory();
  const { isLoading: isDefaultAttributeMappingLoading, defaultAttributeMapping } = useSAMLDefaultAttributeMapping();

  if (isLoading || isDefaultAttributeMappingLoading) {
    return <Spinner />;
  }

  const onFormSubmit = (values: SAMLBackendFormValues, { setErrors, setSubmitting }) => {
    samlAuthBackendSubmit(values)
      .then(() => {
        history.push(Routes.SYSTEM.AUTHENTICATION.BACKENDS.OVERVIEW);
      })
      .catch((error: FetchError) => {
        if (typeof error?.additional?.body?.errors === 'object') {
          setErrors(
            formatAuthBackendValidationErrors(error.additional.body.errors, (v) => ({
              // Add certificate errors to custom form field names to show them in the UI
              idpCertificateFilename: v.idpCertificates,
              spCredentialCertificateFilename: v.spCredentials,
              ...v,
            })),
          );
        } else {
          setSubmitErrors(error);
        }
      })
      .finally(() => setSubmitting(false));
  };

  const handleMetadataFetch = (values: SAMLBackendFormValues, setFieldValue) => () => {
    setIsFetchingMetadata(true);
    SAMLConfig.getIdpMetadata({ url: values.idpMetadataUrl })
      .then((idpMetadata) => {
        setFieldValue('idpSsoServiceRedirectBindingUrl', idpMetadata.sso_service_redirect_binding_url);
        setFieldValue('idpSsoServicePostBindingUrl', idpMetadata.sso_service_post_binding_url);
        setFieldValue('idpLogoutServiceRedirectBindingUrl', idpMetadata.logout_service_redirect_binding_url);
        setFieldValue('idpLogoutServicePostBindingUrl', idpMetadata.logout_service_post_binding_url);
        setFieldValue('idpEntityId', idpMetadata.entity_id);
        setFieldValue('idpCertificate', idpMetadata.certificates?.[0]);
        setFieldValue('idpCertificateFilename', idpMetadata.certificates && 'cert-from-metadata.pem');
        setMetadataFetchErrors(undefined);
      })
      .catch((error) => {
        if (error.additional?.body?.message) {
          setMetadataFetchErrors(error.additional.body.message);
        } else {
          setMetadataFetchErrors(error.toString() as string);
        }
      })
      .finally(() => setIsFetchingMetadata(false));
  };

  const handleSpCredentialFetch = (setFieldValue) => () => {
    SAMLConfig.generateSpCredentials()
      .then((credentialResponse) => {
        setFieldValue('spCredentialCertificate', credentialResponse.certificate);
        setFieldValue('spCredentialCertificateFilename', 'generated-sp-certificate.pem');
        setFieldValue('spCredentialPrivateKey', { set_value: credentialResponse.private_key });
        setFieldValue('spCredentialPrivateKeyFilename', 'generated-sp-key.pem');
        setSpCredentialsGenerateErrors(undefined);
      })
      .catch((error) => {
        if (error.additional?.body?.message) {
          setSpCredentialsGenerateErrors(error.additional.body.message);
        } else {
          setSpCredentialsGenerateErrors(error.toString() as string);
        }
      });
  };

  const submitErrorComponent = submitErrors && (
    <AuthBackendSubmitError error={submitErrors} backendId={authBackend.id} />
  );

  const defaultCreateRoleId = roles.find((role) => role.name === 'Reader')?.id;
  const defaultRolesValues = authBackend.defaultRoles;

  const initialValues = {
    ...authBackend,
    defaultRoles: defaultRolesValues === '' ? defaultCreateRoleId : defaultRolesValues,
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onFormSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}>
      {({ isSubmitting, values, setFieldValue, errors }) => (
        <Wizard<StepKey>
          hidePreviousNextButtons
          justified
          horizontal
          containerClassName=""
          onStepChange={setActiveStep}
          activeStep={activeStep}
          steps={[
            {
              key: 'idp_config',
              title: <span>IdP Configuration</span>,
              component: (
                <IdPConfigForm
                  values={values}
                  roles={roles}
                  isSubmitting={isSubmitting}
                  isFetchingMetadata={isFetchingMetadata}
                  validationErrors={<ValidationErrorSummary errors={errors} />}
                  submitErrors={submitErrorComponent}
                  metadataFetchErrors={metadataFetchErrors}
                  spCredentialsGenerateErrors={spCredentialsGenerateErrors}
                  setActiveStep={setActiveStep}
                  setFieldValue={setFieldValue}
                  handleMetadataFetch={handleMetadataFetch}
                  handleSpCredentialFetch={handleSpCredentialFetch}
                  defaultAttributeMapping={defaultAttributeMapping}
                />
              ),
            },
            {
              key: 'team_sync',
              title: (
                <span>
                  Team Sync <small>(Optional)</small>
                </span>
              ),
              component: (
                <TeamSyncForm
                  isSubmitting={isSubmitting}
                  defaultAttributeMapping={defaultAttributeMapping}
                  setActiveStep={setActiveStep}
                  licenseIsValid={licenseIsValid}
                />
              ),
            },
          ]}
        />
      )}
    </Formik>
  );
};

export default SAMLBackendConfigWizard;
